'use client';
import type { TeamClaimStatus } from '@baseball-near-you/common';
import type { Team, TeamClaim } from '@baseball-near-you/prisma';
import { cn } from '@baseball-near-you/ui';
import dayjs from 'dayjs';
import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useRef } from 'react';
import { claimStatusStyleMap } from '.';

export function ClaimCard({ claim }: { claim: TeamClaim & { team: Team } }) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const claimId = searchParams.get('claim');
  const isSelected = claimId === claim.id.toString();
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isSelected) {
      cardRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }, [isSelected]);

  return (
    <div
      key={claim.id}
      ref={cardRef}
      className={cn(
        'flex flex-col justify-between overflow-hidden rounded-lg border shadow-xs transition-colors',
        isSelected ? 'border-2 border-red' : 'border-gray-300'
      )}
    >
      <div className="flex flex-col">
        <div
          className={cn(
            'flex flex-col border-b border-b-gray-200 px-4 pt-2 pb-3'
          )}
        >
          <div className="mb-2 flex h-8 w-full items-center justify-between">
            <p className="title mt-0 h-full font-medium text-gray-800 text-lg">
              {claim.isCreation ? 'CREATION' : 'VERIFICATION'}
            </p>
            <div className="flex items-center gap-2">
              <p
                className={cn(
                  'title flex h-full items-center rounded-md border border-gray-200 px-3 text-gray-900 text-lg',
                  claimStatusStyleMap[claim.status as TeamClaimStatus]
                )}
              >
                {claim.status}
              </p>
              {/* 
              <button
                type="button"
                onClick={() => {
                  router.push(
                    `${window.location.pathname}?${new URLSearchParams({
                      claimId: claim.id.toString(),
                    })}`
                  );
                }}
              >
                <Link className="size-4 stroke-2 text-gray-800" />
              </button> */}
            </div>
          </div>
          <p className="y-2 truncate font-bold text-gray-900 text-xl">
            {claim.team.name}
          </p>
        </div>
        <div className="flex flex-col px-4 pt-2.5 pb-4">
          {claim.background && (
            <div className="mb-4 flex flex-col">
              <p className="mb-0.5 w-full font-semibold text-gray-800 text-lg">
                Your provided background with this team:
              </p>
              <p className="pl-0.5 font-medium text-gray-700 text-sm">
                {claim.background}
              </p>
            </div>
          )}
          {claim.reasoning && (
            <div className="mb-4 flex flex-col">
              <p className="mb-0.5 w-full font-semibold text-gray-800 text-lg">
                Our determination:
              </p>
              <p className="pl-0.5 font-medium text-gray-700 text-sm">
                {claim.reasoning}
              </p>
            </div>
          )}
          <p className="font-medium text-gray-500 text-xs italic">
            Submitted:{' '}
            {dayjs(claim.createdAt).format('MMMM DD, YYYY hh:mm:ss A')}
          </p>
        </div>
      </div>
    </div>
  );
}
